import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ServicePathProviderService } from '@blk/cwp-ng-framework';
import { InjectConfig, PaConfigKey } from '@ddt-web/config-layer';
import { WebUserDataConstants } from '@ddt-web/domain';
import * as dayjs from 'dayjs';
import * as isEmpty from 'lodash/isEmpty';
import { Observable } from 'rxjs';

import { PEER_GROUP_PERCENTILES } from '../constants';
import { PeerGroups } from '../enums';

@Injectable({
	providedIn: 'root'
})
export class PeerComparisonAnalysisService {
	@InjectConfig(PaConfigKey.CAP_OVERRIDE_DATE) private readonly capOverrideDate: string;
	@InjectConfig(PaConfigKey.ENABLE_CAP_OVERRIDE_DATE) private readonly enableCapOverrideDate: boolean = false;

	private apiGatewayPath: string;

	constructor(private http: HttpClient, private servicePathProvider: ServicePathProviderService) {
		this.apiGatewayPath = this.servicePathProvider.getServicePath('api-gateway');
	}

	/**
	 * Retrieves the peerComparisonGroupData for a given peerComparisonGroup definition filter
	 * @ param peerComparisonGroupFilter - Solr expression to filter the portfolios that will define the peerComparisonGroup
	 */
	public getPeerComparisonGroupData(): Observable<any> {
		const url = this.apiGatewayPath + WebUserDataConstants.USER_DATA_URL.GET_ALL + '?rcs=bfa:cap:analysis';
		return this.http.get(url);
	}

	public getPeerComparisonGroupAnalysisFromResponse(peerGroupResponse: any, positionDetailsResponse: any): PeerComparisonAnalysis[] {
		const positionDetailsEntities: any = Object.values(positionDetailsResponse.entities);

		let asOfDate = positionDetailsEntities?.[0]?.children?.[0]?.sourceDates?.dataAsOf;

		if (this.enableCapOverrideDate) {
			asOfDate = this.capOverrideDate;
		}

		const children = Array.isArray(peerGroupResponse) ? peerGroupResponse.find(child => dayjs(child.asOfDate?.split('T')?.[0]).format('YYYY-MM-DD') === asOfDate) : {};
		let result = [];

		if (!isEmpty(children)) {
			result = Object.keys(PeerGroups).map(group => {
				const response = children?.[group] ? JSON.parse(children?.[group]) : {};

				// Scale % of ETFs
				const etfs: MetricStat = {} as any;
				const risk: MetricStat = {} as any;

				etfs.min = response?.ETFS?.[0]?.MINIMUM * 100.0;
				etfs.mean = response?.ETFS?.[0]?.AVERAGE * 100.0;
				etfs.max = response?.ETFS?.[0]?.MAXIMUM * 100.0;
				etfs.stddev = response?.ETFS?.[0].STDEV * 100.0;

				risk.min = response?.TOTAL_RISK?.[0]?.MINIMUM * 100.0;
				risk.mean = response?.TOTAL_RISK?.[0]?.AVERAGE * 100.0;
				risk.max = response?.TOTAL_RISK?.[0]?.MAXIMUM * 100.0;
				risk.stddev = response?.TOTAL_RISK?.[0].STDEV * 100.0;

				PEER_GROUP_PERCENTILES.forEach(percentile => {
					etfs[percentile] = response.ETFS?.[0]?.[percentile] * 100.0,
					risk[percentile] = response.TOTAL_RISK?.[0]?.[percentile] * 100.0;
				});

				return {
					peerComparisonGroup: group,
					numFound: response?.ETFS?.[0]?.NUMBERPORTFOLIOS,
					asOfDate: children?.asOfDate ? new Date(children.asOfDate.split('T')?.[0]) : '',
					fees: response?.EXPENSE_RATIO?.[0],
					risk,
					yield: response?.TRAILING_YIELD_12M?.[0],
					taxes: response?.TAXCOSTRATIOY1?.[0],
					etfs
				} as PeerComparisonAnalysis;
			});
		}
		return result;
	}
}

export interface PeerComparisonAnalysis {
	numFound: number;
	asOfDate: Date;
	fees: MetricStat;
	risk: MetricStat;
	yield: MetricStat;
	taxes: MetricStat;
	etfs: MetricStat;
	peerComparisonGroup: string;
}

export interface MetricStat {
	[x: string]: string | number;
}
